import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged as onFirebaseAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updatePassword as firebaseUpdatePassword,
  updateProfile as firebaseUpdateProfile,
} from 'firebase/auth';

import { firebaseAuth } from './lib/firebase';

const apiBaseUrl = (process.env.REACT_APP_API_ENDPOINT || '/api').replace(/\/$/, '');

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

const authListeners = new Set();
let currentAuthModel = null;
let profilePromise = null;

const notifyAuthSubscribers = () => {
  authListeners.forEach(listener => {
    try {
      listener(currentAuthModel);
    } catch (error) {
      console.error('[auth] listener error', error);
    }
  });
};

const mapBackendUserToAuthModel = payload => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const displayName = payload.display_name || payload.displayName || payload.name || payload.email || '';
  const companyId = payload.company_id || payload.companyId || '';
  const phoneNumber = payload.phone_number || payload.phoneNumber || '';
  return {
    id: payload.id || '',
    email: payload.email || '',
    role: payload.role || payload.user_role || '',
    name: displayName,
    displayName,
    username: payload.username || '',
    phoneNumber,
    phone_number: phoneNumber,
    company: companyId,
    companyId,
    company_id: companyId,
    company_name: payload.company_name || payload.companyName || '',
    companyName: payload.company_name || payload.companyName || '',
    avatar: payload.avatar_url || payload.avatar || '',
    avatar_url: payload.avatar_url || payload.avatar || '',
    profileHeroURL: payload.profile_hero_url || payload.profileHeroURL || '',
    profile_hero_url: payload.profile_hero_url || payload.profileHeroURL || '',
    base_operacion: payload.base_operacion || payload.baseOperacion || '',
    collectionId: payload.collection_id || payload.collectionId || 'users',
    collectionName: 'users',
  };
};

const fetchBackendUserProfile = async forceRefreshToken => {
  const token = await getFirebaseIdToken(forceRefreshToken);
  if (!token) {
    return null;
  }
  const response = await fetch(`${apiBaseUrl}/users/me`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Client': 'flotte-web',
    },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const status = response.status;
    if (status === 401 || status === 403 || status === 404) {
      return null;
    }
    const detail = payload?.detail || payload?.message || 'No se pudo obtener el perfil.';
    const error = new Error(detail);
    error.status = status;
    error.payload = payload;
    throw error;
  }
  return payload;
};

export const getCurrentAuthUser = () => currentAuthModel;

export function onAuthStateChanged(cb) {
  if (typeof cb !== 'function') {
    return () => {};
  }
  authListeners.add(cb);
  cb(currentAuthModel);
  return () => {
    authListeners.delete(cb);
  };
}

export async function refreshAuthState({ forceRefreshToken = false } = {}) {
  if (profilePromise) {
    try {
      return await profilePromise;
    } catch {
      // ignore, allow new attempt
    }
  }
  profilePromise = (async () => {
    try {
      const profile = await fetchBackendUserProfile(forceRefreshToken);
      currentAuthModel = mapBackendUserToAuthModel(profile);
    } catch (error) {
      currentAuthModel = null;
      throw error;
    } finally {
      notifyAuthSubscribers();
    }
    return currentAuthModel;
  })();

  try {
    return await profilePromise;
  } finally {
    profilePromise = null;
  }
}

onFirebaseAuthStateChanged(firebaseAuth, async firebaseUser => {
  if (!firebaseUser) {
    currentAuthModel = null;
    notifyAuthSubscribers();
    return;
  }
  try {
    await refreshAuthState();
  } catch (error) {
    console.warn('No se pudo sincronizar el perfil del usuario.', error);
  }
});

export function onFirebaseUserChanged(cb) {
  return onFirebaseAuthStateChanged(firebaseAuth, cb);
}

export async function getFirebaseIdToken(forceRefresh = false) {
  const current = firebaseAuth.currentUser;
  if (!current) {
    return null;
  }
  return current.getIdToken(forceRefresh);
}

export async function getApiAuthToken(forceRefresh = false) {
  return getFirebaseIdToken(forceRefresh);
}

export async function signIn(email, password) {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  if (!normalizedEmail || !password) {
    throw new Error('Debes proporcionar un correo y contraseña válidos.');
  }

  const credential = await firebasePasswordSignIn(normalizedEmail, password);
  await refreshAuthState({ forceRefreshToken: true }).catch(() => {});
  return credential;
}

export async function signUpUser({ email, password, displayName, phoneNumber, role }) {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  if (!normalizedEmail) {
    throw new Error('El correo electrónico es obligatorio.');
  }

  const payload = {
    email: normalizedEmail,
    password,
    display_name: displayName,
    phone_number: phoneNumber,
    role,
  };

  const response = await fetch(`${apiBaseUrl}/users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Client': 'flotte-web',
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const message = data?.detail || data?.message || 'No se pudo crear la cuenta.';
    throw new Error(message);
  }

  try {
    await createUserWithEmailAndPassword(firebaseAuth, normalizedEmail, password);
    if (displayName) {
      await firebaseUpdateProfile(firebaseAuth.currentUser, { displayName });
    }
  } catch (error) {
    console.warn('No se pudo iniciar la sesión de Firebase en el registro.', error);
  }

  await refreshAuthState({ forceRefreshToken: true }).catch(() => {});

  return data;
}

export function getCurrentUserToken(forceRefresh = false) {
  return getFirebaseIdToken(forceRefresh);
}

export async function signOutUser() {
  currentAuthModel = null;
  notifyAuthSubscribers();
  try {
    await firebaseSignOut(firebaseAuth);
  } catch (error) {
    console.warn('No se pudo cerrar sesión en Firebase:', error);
  }
}

export async function updateCurrentUserProfile(newDisplayName) {
  const trimmedName = String(newDisplayName || '').trim();
  if (!trimmedName) {
    throw new Error('El nombre es obligatorio.');
  }
  const token = await getFirebaseIdToken();
  if (!token) {
    throw new Error('No hay usuario autenticado.');
  }

  const response = await fetch(`${apiBaseUrl}/users/me`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Client': 'flotte-web',
    },
    body: JSON.stringify({ display_name: trimmedName }),
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const message = payload?.detail || payload?.message || 'No se pudo actualizar tu perfil.';
    throw new Error(message);
  }

  const firebaseUser = firebaseAuth.currentUser;
  if (firebaseUser) {
    try {
      await firebaseUpdateProfile(firebaseUser, { displayName: trimmedName });
    } catch (error) {
      console.warn('No se pudo actualizar el perfil local en Firebase.', error);
    }
  }

  await refreshAuthState();
  return payload;
}

export async function changeUserPassword(currentPwd, newPwd) {
  const firebaseUser = firebaseAuth.currentUser;
  if (!firebaseUser?.email) {
    throw new Error('Usuario no autenticado');
  }

  const normalizedEmail = String(firebaseUser.email || '').trim().toLowerCase();
  if (!normalizedEmail) {
    throw new Error('No hay un correo asociado a la cuenta.');
  }

  try {
    await signInWithEmailAndPassword(firebaseAuth, normalizedEmail, currentPwd);
  } catch (error) {
    throw new Error('La contraseña actual no es válida.');
  }

  const token = await getFirebaseIdToken(true);
  if (!token) {
    throw new Error('No hay usuario autenticado.');
  }

  const response = await fetch(`${apiBaseUrl}/users/me/password`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Client': 'flotte-web',
    },
    body: JSON.stringify({ new_password: newPwd }),
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const message = payload?.detail || payload?.message || 'No se pudo actualizar tu contraseña.';
    throw new Error(message);
  }

  try {
    if (firebaseAuth.currentUser) {
      await firebaseUpdatePassword(firebaseAuth.currentUser, newPwd);
    }
  } catch (error) {
    console.warn('No se pudo actualizar la contraseña local en Firebase.', error);
  }

  return payload;
}
export async function signInWithGoogle(_role) {
  try {
    const credential = await signInWithPopup(firebaseAuth, googleProvider);
    await credential.user.getIdToken(true);
    await refreshAuthState({ forceRefreshToken: true }).catch(() => {});
    return credential.user;
  } catch (error) {
    console.error('[signInWithGoogle] fallo inesperado', error);
    throw error;
  }
}

export async function firebasePasswordSignIn(email, password) {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  if (!normalizedEmail || !password) {
    throw new Error('Debes proporcionar un correo y contraseña válidos.');
  }
  try {
    const credential = await signInWithEmailAndPassword(firebaseAuth, normalizedEmail, password);
    return credential.user;
  } catch (error) {
    const message =
      error?.message ||
      'No pudimos iniciar sesión con Firebase. Verifica tus credenciales e intenta nuevamente.';
    throw new Error(message);
  }
}
